import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCancel,
  faPencil,
  faSave,
  faTimes,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
  GridToolbar,
} from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../globalVar';
import customAxios from '../../utils/customAxios';
import variables from '../../scss/_variables.scss';
import { getToast } from '../../utils/toastConfig';
import { useTableLocale } from '../../hooks/useTableLocale';

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const ConfirmModal = ({ showConfirm, setShowConfirm, handleSubmit }) => {
  return (
    <Dialog open={showConfirm} maxWidth='sm' fullWidth>
      <DialogTitle display='flex' alignItems='center' gap={2}>
        <FontAwesomeIcon icon={faWarning} size='2x' color={variables.redDabi} />
        <Typography className='inventory-movmodal-title'>Importante</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='h6'>¿Desea guardar los cambios?</Typography>
        <ul style={{ marginTop: 10 }}>
          <li>Los cambios realizados se aplicarán inmediatamente.</li>
          <li>No podrás recuperar las respuestas anteriores.</li>
          <li>
            Los reportes generados de esta servicio tendrán estos nuevos valores
            en lugar de los anteriores.
          </li>
        </ul>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          onClick={() => {
            handleSubmit();
            setShowConfirm((prev) => !prev);
          }}
        >
          <Typography>Confirmar cambios</Typography>
        </Button>
        <Button
          variant='outlined'
          onClick={() => setShowConfirm((prev) => !prev)}
        >
          <Typography>Regresar a editar</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditReportModal = ({ visitId, showModal, setShowModal }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [editedAnswers, setEditedAnswers] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});

  const locale = useTableLocale();

  useEffect(() => {
    if (showModal) {
      fetchAnswers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const fetchAnswers = async () => {
    setIsFetching(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}applications/${visitId}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setAnswers([]);
        return;
      }

      setAnswers(response.data.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsFetching(false);
    }
  };

  const handleClose = (finished) => {
    setShowModal(false);
    if (finished) {
      getToast({
        status: 'success',
        title: 'Respuestas actualizadas exitosamente',
      });
    }
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}applications/update/answers`;
      const response = await customAxios.patch(
        endpoint,
        {
          answers: editedAnswers,
        },
        HEADERS
      );
      if (response.status === 200) {
        handleClose(true);
      }
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  const processRowUpdate = (newRow) => {
    setAnswers(answers.map((row) => (row.id === newRow.id ? newRow : row)));

    setEditedAnswers((prev) => {
      const exists = prev.find((row) => row.id === newRow.id);
      if (exists) {
        return prev.map((row) => (row.id === newRow.id ? newRow : row));
      }
      return [...prev, newRow];
    });

    return newRow;
  };

  const columns = [
    { field: 'id' },
    { field: 'description', headerName: 'Pregunta', flex: 1 },
    { field: 'answer_str', headerName: 'Respuesta', flex: 1, editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<FontAwesomeIcon icon={faSave} />}
              label='Save'
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<FontAwesomeIcon icon={faCancel} />}
              label='Cancel'
              className='textPrimary'
              onClick={handleCancelClick(id)}
              color='inherit'
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<FontAwesomeIcon icon={faPencil} />}
            label='Edit'
            className='textPrimary'
            onClick={handleEditClick(id)}
            color='inherit'
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Dialog open={showModal} fullScreen onClose={() => handleClose(false)}>
        <DialogTitle>
          <Typography className='inventory-movmodal-title'>
            Editando respuestas del servicio: {visitId}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => handleClose(false)}
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            width: 40,
            height: 40,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <DialogContent dividers sx={{ padding: '0.5rem' }}>
          <Box>
            <ul style={{ marginTop: 10 }}>
              <li>Los cambios realizados se aplicarán inmediatamente.</li>
              <li>No podrás recuperar las respuestas anteriores.</li>
              <li>
                Los reportes generados de este servicio tendrán estos nuevos
                valores en lugar de los anteriores.
              </li>
            </ul>
          </Box>
          <DataGridPro
            localeText={locale.components.MuiDataGrid.defaultProps.localeText}
            loading={isFetching}
            rows={answers}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => console.log(error)}
            columns={columns}
            density='compact'
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Stack
                  height='100%'
                  alignItems='center'
                  justifyContent='center'
                >
                  No hay respuestas de este servicio
                </Stack>
              ),
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: { showQuickFilter: true },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            onClick={() => setShowConfirmModal(true)}
            disabled={isLoading}
          >
            <Typography>Guardar</Typography>
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmModal
        showConfirm={showConfirmModal}
        setShowConfirm={setShowConfirmModal}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export default EditReportModal;
